import type { DepartureDateFilterOptions } from '@/scopes/search/components/DepartureDateFilter/types'
import { DatePicker } from '@sunwebgroup/uds-ui/date-picker'
import { getDaysAround, removePastDatesFromRange } from '@sunwebgroup/ui-components'
import { MonthPicker, useBreakpoints } from '@sunwebgroup/ui-components/uds'
import { add, format } from 'date-fns'
import * as React from 'react'
import { useIntl } from '@/intl'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import showWeeks from '@/scopes/search/domain/showWeeks'
import { useDepartureDateFilter } from './hooks'

export function DatesCalendar({ options, className }: { options: DepartureDateFilterOptions, className?: string }) {
  const intl = useIntl()
  const { isDesktop } = useBreakpoints()
  const siteName = useConfiguration('siteName')

  const { availableDates, departureDate, flexibleRange, originDate, setOriginDate, setDepartureDate }
    = useDepartureDateFilter({ options })

  function setSelectedDate(date: string) {
    setOriginDate(date)

    if (flexibleRange !== 'exact') {
      const numberOfDays = Number.parseInt(flexibleRange)
      const dateRange = getDaysAround(date, numberOfDays)
      const minimDepartureDate = add(new Date(), { days: 1 })
      const filteredDates = removePastDatesFromRange(dateRange, minimDepartureDate)
      setDepartureDate(filteredDates)
      return
    }

    setDepartureDate(date)
  }

  React.useEffect(() => {
    if (!originDate && departureDate) {
      setOriginDate(Array.isArray(departureDate) ? departureDate?.[0] : departureDate)
    }
  }, [])

  const showWeekNumbers = showWeeks(siteName)

  function toDateOrNull(dateString?: string): Date | null {
    return dateString ? new Date(dateString) : null
  }

  function getDateString(date: Date): string {
    return format(date, 'yyyy-MM-dd')
  }

  function handleDatePickerChange(date: Date | null) {
    if (!date) {
      return
    }

    setSelectedDate(getDateString(date))
  }

  const includeDates = React.useMemo(() => availableDates.map(date => new Date(date)), [availableDates])
  const selectedDate = toDateOrNull(originDate ?? (Array.isArray(departureDate) ? departureDate[0] : departureDate))
  const highlightStartDate = Array.isArray(departureDate)
    ? toDateOrNull(departureDate[0])
    : selectedDate
  const highlightEndDate = Array.isArray(departureDate)
    ? toDateOrNull(departureDate[1])
    : selectedDate

  return (
    <DatePicker
      inline
      className={className}
      includeDates={includeDates}
      value={selectedDate}
      highlightStartDate={highlightStartDate}
      highlightEndDate={highlightEndDate}
      locale={intl.locale}
      onChange={handleDatePickerChange}
      nextMonthAriaLabel={intl.formatMessage({ id: 'quicksearch_filter_departure_date_next_month' })}
      previousMonthAriaLabel={intl.formatMessage({ id: 'quicksearch_filter_departure_date_previous_month' })}
      monthsShown={isDesktop ? 2 : 1}
      showWeekNumbers={showWeekNumbers}
      weekLabel={showWeekNumbers ? intl.formatMessage({ id: 'departure_date_filter_week' }) : ''}
    />
  )
}

interface MonthCalendarProps { options: DepartureDateFilterOptions, name: string, className?: string }

export function MonthCalendar({ options, name, className }: MonthCalendarProps) {
  const intl = useIntl()
  const { availableDates, maxDateString, minDateString, departureDate, setDepartureDate, setOriginDate }
    = useDepartureDateFilter({
      options,
    })

  function setSelectedDate(dates: string | [string, string]) {
    setOriginDate(Array.isArray(dates) ? dates[0] : dates)
    setDepartureDate(dates)
  }

  return (
    <MonthPicker
      name={name}
      locale={intl.locale}
      availableDates={availableDates}
      maxDateString={maxDateString}
      minDateString={minDateString}
      selectedMonth={departureDate}
      setSelectedDates={setSelectedDate}
      className={className}
    />
  )
}
